import React, { createContext, useContext, useEffect, useState } from 'react';
import { applyPaletteVars, type PaletteKey } from './palettes';

const LS_KEY_DARK = 'amax_theme_dark';
const LS_KEY_PALETTE = 'amax_theme_key';
const LS_KEY_SIDEBAR_TRANSPARENT = 'amax_sidebar_transparent';
const VALID_PALETTES: PaletteKey[] = ['default', 'lime', 'slip', 'indigo', 'sunset', 'mono', 'rose'];

function getInitialDark(): boolean {
  try {
    const stored = localStorage.getItem(LS_KEY_DARK);
    if (stored !== null) return stored === 'true';
  } catch { /* localStorage unavailable */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

function getInitialPaletteKey(): PaletteKey {
  try {
    const stored = localStorage.getItem(LS_KEY_PALETTE) as PaletteKey | null;
    if (stored && VALID_PALETTES.includes(stored)) return stored;
  } catch { /* ignore */ }
  return 'default';
}

function getInitialSidebarTransparent(): boolean {
  try {
    const stored = localStorage.getItem(LS_KEY_SIDEBAR_TRANSPARENT);
    if (stored !== null) return stored === 'true';
  } catch { /* ignore */ }
  return false;
}

interface ThemeContextValue {
  isDark: boolean;
  themeKey: PaletteKey;
  sidebarTransparent: boolean;
  toggleTheme: () => void;
  setThemeKey: (key: PaletteKey) => void;
  setSidebarTransparent: (v: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  isDark: true,
  themeKey: 'default',
  sidebarTransparent: false,
  toggleTheme: () => {},
  setThemeKey: () => {},
  setSidebarTransparent: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(() => {
    const dark = getInitialDark();
    applyPaletteVars(getInitialPaletteKey(), dark);
    return dark;
  });
  const [themeKey, setThemeKeyState] = useState<PaletteKey>(getInitialPaletteKey);
  const [sidebarTransparent, setSidebarTransparentState] = useState(getInitialSidebarTransparent);

  useEffect(() => {
    applyPaletteVars(themeKey, isDark);
    try {
      localStorage.setItem(LS_KEY_DARK, String(isDark));
      localStorage.setItem(LS_KEY_PALETTE, themeKey);
    } catch { /* ignore */ }
  }, [isDark, themeKey]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_SIDEBAR_TRANSPARENT, String(sidebarTransparent));
    } catch { /* ignore */ }
  }, [sidebarTransparent]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === LS_KEY_DARK && e.newValue !== null) {
        setIsDark(e.newValue === 'true');
      }
      if (e.key === LS_KEY_PALETTE && e.newValue && VALID_PALETTES.includes(e.newValue as PaletteKey)) {
        setThemeKeyState(e.newValue as PaletteKey);
      }
      if (e.key === LS_KEY_SIDEBAR_TRANSPARENT && e.newValue !== null) {
        setSidebarTransparentState(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <ThemeContext.Provider value={{
      isDark,
      themeKey,
      sidebarTransparent,
      toggleTheme: () => setIsDark((v) => !v),
      setThemeKey: setThemeKeyState,
      setSidebarTransparent: setSidebarTransparentState,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export type { PaletteKey };
